import {SectionBgView} from './SectionBgView'
import {PIXI} from '@colonist/pixi'
import {UIGameConstants} from '../ui_game/UIGameConstants'
import {GameFont} from '../GameFont'
import {ModeSettingEnum} from '../../../../shared/GameEnums'
import {CardView} from './CardView'
import {TextView} from './TextView'
import {UIGameManager} from '../ui_game/UIGameManager'
import {CardDataController, BaseCardData} from '@colonist/game-card-types'
import {GameColor} from '../GameColor'
import {TranslationObject} from '@colonist/i18n'
import {bringToFront} from '../ui_utils/PixiUtils'
import {CardEnum, CardTypeEnum} from '@colonist/game-states'
import {CARD_TIP_BODY, CARD_TIP_TITLE} from '@colonist/ui-game'

export class CardCountView extends SectionBgView {
    private readonly uiGameManager: UIGameManager

    topCard: CardView
    private stackCards: CardView[] = []
    private readonly stackLimits: number[]

    count: TextView
    cardEnum: CardEnum
    private readonly cardData: BaseCardData | undefined

    private readonly cardHeight: number
    private readonly cardWidth: number
    private readonly cardY: number

    private readonly hideBankCards: boolean | undefined

    private readonly defaultTintValue: number = 16777215

    private readonly distanceBetweenCardsInStack: number

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, container: PIXI.Container, cardEnum: CardEnum, textColor?: string, isTradeView: boolean = true, hideBankCards?: boolean) {
        super(x, y, w, h, container)

        this.uiGameManager = uiGameManager

        this.cardEnum = cardEnum
        this.cardData = CardDataController.getCardDataForCard(cardEnum)

        this.cardHeight = this.height * 0.65
        this.cardWidth = this.cardHeight / UIGameConstants.CARD_RATIO
        this.cardY = (h - this.cardHeight) / 2
        this.distanceBetweenCardsInStack = this.cardHeight / 10

        this.hideBankCards = hideBankCards
        this.stackLimits = this.getStackLimits(this.uiGameManager.gameState.gameModeData.modeSetting)

        if(isTradeView) this.createTradeView(textColor)
        else this.createTopCard()
    }

    update(amountOfCards: number) {
        const stackSize = this.getStackSize(amountOfCards)
        this.updateStackView(stackSize)

        if(this.hideBankCards) return
        this.topCard.updateBadge(amountOfCards)
    }

    private updateStackView(stackSize: number) {
        this.removeStackCards()

        if(stackSize == 0) {
            this.fadeInCard()
            return
        }

        this.fadeOutCard()

        this.createStackCards(stackSize)

        bringToFront(this.topCard)
    }

    private createStackCards(amountOfCards: number) {
        const stackCardTint = GameColor.getColorHexValue(GameColor.Color.Gray2)

        for(let i = amountOfCards - 1; i >= 1; i--) {
            const card = new CardView(this.uiGameManager, 0, 0, this.cardY - this.distanceBetweenCardsInStack * i, this.cardWidth, this.cardHeight, this.cardEnum, this)
            card.hideBadge()
            card.view.tint = stackCardTint
            this.stackCards.push(card)
        }
    }

    private removeStackCards() {
        for(const card of this.stackCards) {
            card.destroy()
        }
        this.stackCards = []
    }

    private createTopCard() {
        const setDefaultTip = false
        const forceCountBadge = true
        this.topCard = new CardView(this.uiGameManager, 0, 0, this.cardY, this.cardWidth, this.cardHeight, this.cardEnum, this, setDefaultTip, forceCountBadge)

        if(this.hideBankCards) this.topCard.hideBadge()
    }

    setTopCardToolTip(amountOfCards: number) {
        if(this.cardData == undefined) return
        const tipTitle = {
            ...CARD_TIP_TITLE[this.cardEnum],
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }
        const tipBody = {
            ...CARD_TIP_BODY[this.cardEnum],
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }

        const tipBodyMessages: TranslationObject[] = [tipBody]
        if(this.hideBankCards) {
            tipBodyMessages.push(this.getExtraTipBody(amountOfCards))
        }

        this.topCard.view.setTip(tipTitle, tipBodyMessages)
    }

    private getExtraTipBody(amountOfCards: number): TranslationObject {
        const stackSize = this.getStackSize(amountOfCards)
        if(stackSize == 0) return {key: 'strings:game.tooltips.cardCountInStack.empty'}
        const lowerBound = this.stackLimits[stackSize - 1] + 1
        const upperBound = this.stackLimits[stackSize]
        return {key: 'strings:game.tooltips.cardCountInStack.full', options: {lowerBound: lowerBound, upperBound: upperBound}}
    }

    setClickAction(action: () => void) {
        this.topCard.view.setClickAction(action)
    }

    repositionStackX() {
        const centerX = (this.width - this.cardWidth) / 2
        this.topCard.x = centerX

        for(const card of this.stackCards) {
            card.x = centerX
        }
    }

    private fadeInCard() {
        this.topCard.view.tint = GameColor.getColorHexValue(GameColor.Color.CardViewFadedTint)
    }

    private fadeOutCard() {
        this.topCard.view.tint = this.defaultTintValue
    }

    private createTradeView(textColor?: string) {
        this.createTopCard()
        this.repositionStackX()
        this.topCard.hideBadge()
        this.createTradeRatioText(textColor)
        this.repositionY()
    }

    private createTradeRatioText(textColor?: string) {
        const countY = this.cardY + this.topCard.height / 2
        const countHeight = this.height / 5

        this.count = new TextView({key: 'strings:colonistCommon.utils.#buffer', options: {value: '200'}}, 0, countY, this.width, countHeight, this, GameFont.Size.L, textColor)
        this.count.centerX()
    }

    private repositionY() {
        const extraY = this.height - (this.count.y + this.count.height)
        this.topCard.y = extraY / 3
        this.count.y += extraY * 2 / 3
    }

    updateTradeRatioText(text: TranslationObject) {
        this.count.setText(text)
        this.count.centerX()
    }

    private getStackSize(amountOfCards: number): number {
        if(amountOfCards == this.stackLimits[0]) return 0
        if(amountOfCards <= this.stackLimits[1]) return 1
        if(amountOfCards <= this.stackLimits[2]) return 2
        return 3
    }

    private getStackLimits(modeSetting: ModeSettingEnum): number[] {
        const cardType = this.cardData?.cardTypeEnum
        switch(cardType) {
            case CardTypeEnum.Resource:
                return CardCountView.getResourceCardStackLimits(modeSetting)
            case CardTypeEnum.Commodity:
                return CardCountView.getCommodityCardStackLimits(modeSetting)
            case CardTypeEnum.Development:
                return CardCountView.getDevelopmentCardStackLimits(modeSetting)
            default:
                return CardCountView.getResourceCardStackLimits(modeSetting)
        }
    }

    static getResourceCardStackLimits(modeSetting: ModeSettingEnum): number[] {
        switch(modeSetting) {
            case ModeSettingEnum.Classic4P:
            case ModeSettingEnum.Seafarers4P:
            case ModeSettingEnum.CitiesAndKnights4P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return [0, 7, 13, 19]
            case ModeSettingEnum.Classic56P:
            case ModeSettingEnum.Seafarers56P:
            case ModeSettingEnum.CitiesAndKnights56P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return [0, 8, 16, 24]
            case ModeSettingEnum.Classic78P: return [0, 10, 20, 29]
            default: return [0, 7, 13, 19]
        }
    }

    static getCommodityCardStackLimits(modeSetting: ModeSettingEnum): number[] {
        switch(modeSetting) {
            case ModeSettingEnum.Classic4P:
            case ModeSettingEnum.Seafarers4P:
            case ModeSettingEnum.CitiesAndKnights4P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return [0, 4, 8, 12]
            case ModeSettingEnum.Classic56P:
            case ModeSettingEnum.Seafarers56P:
            case ModeSettingEnum.CitiesAndKnights56P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return [0, 6, 12, 18]
            case ModeSettingEnum.Classic78P: return [0, 8, 16, 24]
            default: return [0, 4, 8, 12]
        }
    }

    static getDevelopmentCardStackLimits(modeSetting: ModeSettingEnum): number[] {
        switch(modeSetting) {
            case ModeSettingEnum.Classic4P:
            case ModeSettingEnum.Seafarers4P:
            case ModeSettingEnum.CitiesAndKnights4P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return [0, 9, 17, 25]
            case ModeSettingEnum.Classic56P:
            case ModeSettingEnum.Seafarers56P:
            case ModeSettingEnum.CitiesAndKnights56P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return [0, 12, 23, 34]
            case ModeSettingEnum.Classic78P: return [0, 15, 29, 43]
            default: return [0, 9, 17, 25]
        }
    }
}
